// Types for Contracts Module

export type ContractStatus = 'pending' | 'paid' | 'completed' | 'expired' | 'cancelled';
export type KYCStatus = null | 'pending' | 'verified' | 'failed';
export type ClientGrade = 'A' | 'B' | 'C' | 'T';
export type PaymentType = 'one_time' | 'subscription' | 'one_time_full';

/** Tipo de contrato: individual, pareja/duo familiar, unidad familiar con hijos, o oferta 2x1 (dos contratos vinculados) */
export type ContractType = 'individual' | 'duo' | 'family' | 'linked';

/** Método de pago para contratos personalizados */
export type CustomPaymentMethod = 'stripe' | 'transferencia' | 'efectivo' | 'bizum' | 'otro';

export const CUSTOM_PAYMENT_METHOD_LABELS: Record<CustomPaymentMethod, string> = {
  stripe: 'Tarjeta (Stripe)',
  transferencia: 'Transferencia bancaria',
  efectivo: 'Efectivo',
  bizum: 'Bizum',
  otro: 'Otro',
};

/** Referencia a un expediente administrativo vinculado al contrato */
export interface ExpedienteReference {
  id: string;
  holder_id?: string;
  status?: 'pending' | 'in_progress' | 'approved' | 'denied';
  numero_expediente_oficial?: string;
  fecha_resolucion?: string;
}

export interface Contract {
  id: string;
  hiring_code: string;
  client_name: string;
  client_email: string;
  service_name: string;
  service_description?: string;
  amount: number; // En céntimos (backend devuelve cents)
  currency: string;
  status: ContractStatus;
  kyc_status: KYCStatus;
  grade?: ClientGrade;
  payment_type?: PaymentType;
  expires_at: string;
  created_at: string;
  updated_at: string;
  short_url: string;
  // Tipo de contrato (multi-contratante)
  contract_type?: ContractType;
  holders?: ContractHolder[];
  dependents?: ContractDependent[];
  expedientes?: ExpedienteReference[];
  // Contratos vinculados (oferta 2x1)
  linked_group_id?: string;
  linked_contract_code?: string;
  linked_partner_name?: string;
  // Datos del cliente
  client_passport?: string;
  client_nie?: string;
  client_nationality?: string;
  client_address?: string;
  client_city?: string;
  client_province?: string;
  client_postal_code?: string;
  // Contrato
  contract_date?: string;
  contract_accepted?: boolean;
  contract_accepted_at?: string;
  contract_pdf_url?: string;
  final_contract_url?: string;
  completed_at?: string;
  // Pago
  manual_payment_confirmed?: boolean;
  manual_payment_note?: string;
  manual_payment_method?: string;
  payment_intent_id?: string;
  subscription_id?: string;
  subscription_status?: string;
  first_payment_amount?: number;
  // Metadata
  notes?: string;
  created_by?: string;
  // Relación con CRM
  contact_id?: string; // UUID del contacto en el CRM
  expediente_id?: string; // UUID del expediente vinculado
  // Mandato (asociado al contrato por hiring_code; backend HiringPayment 2026-02-08)
  mandate_status?: 'pending' | 'signed' | 'revoked' | null;
  mandate_pdf_url?: string | null;
  mandate_signed_at?: string | null;
  /** Cuando hay varios titulares: códigos de mandato por persona (uno por holder). Si no existe, se usa hiring_code para el titular principal. */
  mandate_codes?: string[] | null;
  /** Mandatos por titular (futuro backend). Si no existe, se infiere un mandato por contrato. */
  mandates?: ContractMandateByHolder[] | null;
  // Anexos (incluidos en GET detalle)
  annexes?: ContractAnnex[];
  // Contrato personalizado (2026-03-13)
  is_custom?: boolean;
  num_installments?: number;
  custom_payment_method?: CustomPaymentMethod;
  /** Idioma del contrato y flujo público (es/en/fr). Default: es */
  language?: 'es' | 'en' | 'fr';
}

/** Mandato asociado a un titular (cuando el backend soporta varios mandatos por contrato). */
export interface ContractMandateByHolder {
  holder_index: number;
  holder_name: string;
  holder_email?: string;
  mandate_code: string;
  mandate_status?: 'pending' | 'signed' | 'revoked' | null;
  mandate_pdf_url?: string | null;
  mandate_signed_at?: string | null;
}

/** Conteo agregado de contratos por estado respetando filtros activos (excepto status). */
export interface ContractStats {
  total: number;
  pending: number;
  paid: number;
  completed: number;
  expired: number;
  cancelled: number;
}

export const EMPTY_CONTRACT_STATS: ContractStats = {
  total: 0,
  pending: 0,
  paid: 0,
  completed: 0,
  expired: 0,
  cancelled: 0,
};

export interface ContractListResponse {
  items: Contract[];
  total: number;
  skip: number;
  limit: number;
  stats?: ContractStats;
}

/** Respuesta de POST /crm/contracts/bulk-expire-expired */
export interface BulkExpireResponse {
  expired_count: number;
}

export interface ContractFilters {
  status?: ContractStatus | 'all';
  kyc_status?: KYCStatus | 'all';
  grade?: ClientGrade | 'all';
  payment_type?: PaymentType | 'all';
  contract_type?: ContractType | 'all';
  search?: string;
  from_date?: string;
  to_date?: string;
  sort_by?: 'created_at_desc' | 'created_at_asc' | 'expires_at_asc' | 'amount_desc';
  skip?: number;
  limit?: number;
}

export interface ContractCreateRequest {
  contract_template: string;
  service_name: string;
  service_description?: string;
  amount?: number;
  currency?: string;
  grade: ClientGrade;
  payment_type?: PaymentType;
  expires_in_days?: number;
  notes?: string;
  client_name: string;
  client_email: string;
  client_passport?: string;
  client_nie?: string;
  client_address?: string;
  client_city?: string;
  client_province?: string;
  client_postal_code?: string;
}

export interface ContractUpdateRequest {
  // Estado y configuración
  status?: ContractStatus;
  payment_type?: PaymentType;
  grade?: ClientGrade;
  contract_type?: ContractType;
  currency?: string;
  amount?: number; // En EUR
  kyc_status?: KYCStatus;
  
  // Cliente
  client_name?: string;
  client_email?: string;
  client_passport?: string;
  client_nie?: string;
  client_nationality?: string;
  client_address?: string;
  client_city?: string;
  client_province?: string;
  client_postal_code?: string;
  
  // Servicio
  service_name?: string;
  service_description?: string;
  
  // Pago manual
  manual_payment_confirmed?: boolean;
  manual_payment_method?: string;
  manual_payment_note?: string;
  
  // Suscripción / Stripe
  subscription_id?: string;
  subscription_status?: string;
  /** ID del cliente en Stripe (cus_xxx) para vincular contrato con Stripe */
  stripe_customer_id?: string;

  // Pago parcial
  first_payment_amount?: number;

  // Expiración
  expires_in_days?: number;

  // Notas
  notes?: string;

  // Titulares (co-contratantes) - se envía como "titulars" al backend
  holders?: ContractHolder[];

  // Dependientes (hijos/menores)
  dependents?: ContractDependent[];

  // Contrato personalizado (2026-03-13)
  is_custom?: boolean;
  num_installments?: number;
  custom_payment_method?: CustomPaymentMethod;
}

export const CONTRACT_STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  expired: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800',
} as const;

export const KYC_STATUS_COLORS = {
  null: 'bg-gray-100 text-gray-800',
  pending: 'bg-yellow-100 text-yellow-800',
  verified: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
} as const;

export const GRADE_COLORS = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-blue-100 text-blue-800',
  C: 'bg-orange-100 text-orange-800',
  T: 'bg-purple-100 text-purple-800',
} as const;

/** Adjunto opcional en anexos (fotos, PDFs subidos a Cloudinary) */
export interface AnnexAttachmentInfo {
  url: string;
  filename: string;
  content_type: string;
  size?: number;
}

// Anexos al contrato (API devuelve id como number)
export interface ContractAnnex {
  id?: number | string;
  hiring_code: string;
  title: string;
  content: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string | null;
  attachments?: AnnexAttachmentInfo[];
}

export interface ContractAnnexCreateRequest {
  hiring_code: string;
  title: string;
  content: string;
  attachments?: AnnexAttachmentInfo[];
}

export interface ContractAnnexUpdateRequest {
  title?: string;
  content?: string;
  attachments?: AnnexAttachmentInfo[];
}

/** Plantilla de anexo desde API backend (GET /admin/hiring/annex-templates) */
export interface AnnexTemplate {
  slug: string;
  name: string;
  category: string;
  category_label: string;
  placeholders: string[];
  content?: string;
}

export interface AnnexTemplateCategory {
  id: string;
  label: string;
  count: number;
}

// --- Multi-firma (HiringPaymentCreate) ---
export type DocumentType = 'nif' | 'nie' | 'passport';

/** Contratante titular del contrato (1 para individual/linked, 2 para duo/family) */
export interface ContractHolder {
  id?: string;
  full_name: string;
  email: string;
  phone?: string;
  document_id: string;
  document_type: DocumentType;
  nationality?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  is_primary: boolean;
  relationship_to_primary?: 'conyuge' | 'pareja_hecho' | 'familiar_directo';
  expediente_id?: string;
  expediente_status?: 'pending' | 'in_progress' | 'approved' | 'denied';
}

export interface ContractTitular {
  full_name: string;
  email: string;
  phone?: string;
  document_id?: string;
  document_type?: DocumentType;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  nationality?: string;
  is_primary: boolean;
}

export interface ContractSigner {
  email: string;
  full_name?: string;
  order?: number;
  status?: 'pending' | 'signed' | 'expired';
  signed_at?: string;
  ip_address?: string;
  user_agent?: string;
  token?: string;
}

/** Dependiente (hijo/a) en contrato tipo family */
export interface ContractDependent {
  full_name: string;
  date_of_birth: string; // YYYY-MM-DD
  relationship: 'hijo' | 'hija' | string;
  document_id?: string;
  document_type?: DocumentType;
  nationality?: string;
  is_minor?: boolean;
}

/** Body para POST /crm/contracts/ (HiringPaymentCreate). user_id y catalog_item_id opcionales; si no hay catalog_item_id se usa service_name (y opcionalmente service_description). */
export interface HiringPaymentCreate {
  user_id?: string;
  catalog_item_id?: number;
  grade: ClientGrade;
  payment_type?: PaymentType;
  contract_template?: string;
  expires_in_days?: number;
  amount?: number; // centavos
  currency?: string;
  /** Tipo de contrato: individual, duo, family, linked (2x1) */
  contract_type?: ContractType;
  client_name: string;
  client_email: string;
  client_passport?: string;
  client_nie?: string;
  client_nationality?: string;
  client_address?: string;
  client_city?: string;
  client_province?: string;
  client_postal_code?: string;
  service_name?: string;
  service_description?: string;
  manual_payment_note?: string;
  manual_payment_method?: string;
  manual_payment_confirmed?: boolean;
  /** Contratantes (holders); para linked se envían 2 personas y el backend genera 2 contratos */
  holders?: ContractHolder[];
  titulars?: ContractTitular[];
  signers?: ContractSigner[];
  dependents?: ContractDependent[];
  /** Solo para linked: UUID compartido entre los 2 contratos (generado por backend si no se envía) */
  linked_group_id?: string;
  /** Solo para linked: datos de la segunda persona (backend crea 2 contratos) */
  linked_second_participant?: {
    client_name: string;
    client_email: string;
    client_passport?: string;
    client_nie?: string;
    payment_type?: PaymentType;
  };
  // Contrato personalizado (2026-03-13)
  is_custom?: boolean;
  num_installments?: number;
  custom_payment_method?: CustomPaymentMethod;
  /** Idioma del contrato y flujo público (es/en/fr). Default: es */
  language?: 'es' | 'en' | 'fr';
}

/** Campo compatible para respuestas legacy y nuevas (snake_case/camelCase). */
export interface ContractCreateRootCodeFields {
  hiring_code?: string;
  hiringCode?: string;
  short_url?: string;
  shortUrl?: string;
  client_name?: string;
  clientName?: string;
}

/** Ítem de código en respuesta de creación linked (2x1). Usado en response.codes[]. */
export interface ContractCreateCodeItem {
  hiring_code?: string;
  hiringCode?: string;
  short_url?: string;
  shortUrl?: string;
  client_name?: string;
  clientName?: string;
}

/** Código normalizado para UI (camelCase). */
export interface ExtractedHiringCodeItem {
  hiringCode: string;
  shortUrl: string;
  clientName?: string;
}

/** Respuesta 201 de POST /crm/contracts/ cuando es oferta 2x1 (linked). */
export interface LinkedContractCreateResponse extends ContractCreateRootCodeFields {
  linked_group_id?: string;
  linkedGroupId?: string;
  contracts?: Array<Partial<Contract>>;
  codes?: ContractCreateCodeItem[];
}

/** Respuesta 201 individual de POST /crm/contracts/ o /crm/hiring/create. */
export type IndividualContractCreateResponse = Partial<Contract> & ContractCreateRootCodeFields;

/** Respuesta 201 de creación: individual o linked (2x1), legacy o nueva. */
export type AdminContractCreateResponse = IndividualContractCreateResponse | LinkedContractCreateResponse;

/** Respuesta 200 de POST /crm/contracts/{code}/resend-contract-email (reenvío correo de bienvenida). */
export interface ResendContractEmailResponse {
  message: string;
  sent_to: string;
}

// --- Portal cliente (GET /clientes/contratos, GET/PATCH /clientes/perfil) ---
export interface ClienteContractResponse {
  id: number;
  hiring_code: string;
  service_name: string;
  service_description?: string;
  amount: number;
  currency: string;
  status: string;
  grade?: string;
  contract_type?: ContractType;
  contract_pdf_url?: string;
  final_contract_url?: string;
  created_at: string;
  completed_at?: string;
  contract_accepted_at?: string;
  expires_at?: string;
  annexes: ContractAnnex[];
  holders?: ContractHolder[];
  dependents?: ContractDependent[];
  linked_contract_code?: string;
  linked_partner_name?: string;
}

export interface ClientePerfilResponse {
  id: string;
  email: string;
  full_name?: string;
  phone_number?: string;
  role: string;
  is_active: boolean;
  is_verified: boolean;
  last_login?: string;
  created_at: string;
}










// --- Contract Installments (cuotas planificadas) ---

export type InstallmentStatus = 'pending' | 'paid' | 'overdue' | 'cancelled';

export interface ContractInstallment {
  id: number;
  hiring_payment_id: number;
  installment_number: number;
  total_installments: number;
  amount_cents: number;
  currency: string;
  status: InstallmentStatus;
  due_date: string;
  paid_at: string | null;
  payment_method: string | null;
  payment_source: 'stripe' | 'manual' | null;
  stripe_invoice_id: string | null;
  note: string | null;
  created_at: string;
  updated_at: string;
  updated_by: string | null;
  /** Factura local vinculada a esta cuota (si fue creada desde aquí). */
  invoice_id: string | null;
  invoice_number: string | null;
}
